<template>
  <div
    class="exhibition-video__player"
    :class="{ 'exhibition-video__player--is-playing': isPlaying }"
  >
    <button
      type="button"
      @click="playVideo"
      class="exhibition-video__play-button"
      title="Play"
      ref="playButton"
    >
      <span class="exhibition-video__play-button-icon"></span>
      <div class="exhibition-video__poster">
        <picture>
          <source :srcset="poster.small" media="(max-width: 480px)" />
          <source :srcset="poster.medium" media="(max-width: 768px)" />
          <b-img-lazy
            class="exhibition-video__poster-image"
            :src="poster.large"
            :alt="poster.alt"
          />
        </picture>
      </div>
    </button>

    <div
      class="exhibition-video__overlay"
      ref="overlay"
      role="dialog"
      aria-label="Video Player"
      :aria-hidden="!isPlaying"
      :tabindex="isPlaying ? 0 : -1"
    >
      <div class="exhibition-video__overlay-wrap" ref="overlayWrap">
        <div class="exhibition-video__embed" ref="embed">
          <component
            v-bind:is="videoComponent"
            :video="video"
            :isPlaying="isPlaying"
          ></component>
        </div>
      </div>
      <button
        type="button"
        @click="exitVideo"
        class="exhibition-video__overlay-close"
        title="Close"
      >
        Close
      </button>
    </div>
  </div>
</template>

<script lang="js">
import supportsIntersectionObserver from '../../js/util/supportsIntersectionObserver';
import supportsResizeObserver from '../../js/util/supportsResizeObserver';

const AhcVideoYoutube = () =>
  import(/* webpackChunkName: "video-youtube-lazy" */ "./../video/video-youtube.vue");
const AhcVideoVimeo = () =>
  import(/* webpackChunkName: "video-vimeo-lazy" */ "./../video/video-vimeo.vue");

const ESCAPE_KEY = 27;

export default  {
  name: 'ahc-exhibition-video',
  components: {
    AhcVideoYoutube,
    AhcVideoVimeo
  },
  props: {
    type: {
      type: String,
      required: true
    },
    poster: Object,
    video: {
      type: Object,
      required: true
    }
  },
  data() {
    // Define a map for easy lookup of video type -> video component.
    const componentLookup = new Map();
    componentLookup.set("youtube", "ahc-video-youtube");
    componentLookup.set("vimeo", "ahc-video-vimeo");

    return {
      componentLookup: componentLookup,
      isPlaying: false,
    }
  },
  computed: {
    videoComponent() {
      let component = null;

      if (this.componentLookup.has(this.type)) {
        component = this.componentLookup.get(this.type);
      }

      return component;
    }
  },
  methods: {
    playVideo() {
      this.isPlaying = true;

      document.addEventListener('focusin', this.restrictFocus, false);
      document.addEventListener('keydown', this.handleKeyDown, false);

      // focus the player element and scroll the element to the top of
      // the viewport
      this.$nextTick(() => {
        this.$refs.overlay.focus();

        const containingEl = this.$el.closest('.exhibition-section__wrapper') || this.$el;
        window.scrollTo(0, containingEl.getBoundingClientRect().top + window.pageYOffset);
      });

      // exit the video when the section is completely out of view
      if (supportsIntersectionObserver) {
        this.exitVideoObserver = new IntersectionObserver((entries, observer) => {
          entries.forEach(entry => {
            if (entry.intersectionRatio !== 1 && this.isPlaying) {
              observer.unobserve(this.$el);
              this.exitVideo();
            }
          });
        }, { threshold: 0 });

        this.exitVideoObserver.observe(this.$el);
      }
    },
    exitVideo() {
      this.isPlaying = false;

      document.removeEventListener('focusin', this.restrictFocus, false);
      document.removeEventListener('keydown', this.handleKeyDown, false);

      this.$nextTick(() => {
        // don't shift the scroll position when the element is focused
        const scrollY = window.pageYOffset;
        this.$refs.playButton.focus();
        window.scrollTo(0, scrollY);
      });
    },
    handleKeyDown(e) {
      if (e.keyCode && e.keyCode === ESCAPE_KEY) {
        this.exitVideo();
      }
    },
    restrictFocus(e) {
      if (
        document !== e.target &&
        this.$refs.overlay !== e.target &&
        this.isPlaying &&
        !this.$refs.overlay.contains(e.target)
      ) {
        e.stopPropagation();
        this.$refs.overlay.focus();
      }
    }
  },
  mounted() {
    if (supportsResizeObserver) {
      this.resizeObserver = new ResizeObserver(entries => {
        entries.forEach(entry => {
          const { width, height } = entry.contentRect;
          let videoWidth, videoHeight;

          if (width > height) {
            videoWidth = Math.min(height * (16/9), width);
            videoHeight = videoWidth * (9/16);
          } else {
            videoHeight = Math.min(width * (9/16), height);
            videoWidth = videoHeight * (16/9);
          }

          this.$refs.embed.style.width = videoWidth + 'px';
          this.$refs.embed.style.height = videoHeight + 'px';
        });
      });

      this.resizeObserver.observe(this.$refs.overlayWrap);
    }
  },
  beforeDestroy() {
    document.removeEventListener('focusin', this.restrictFocus, false);
    document.removeEventListener('keydown', this.handleKeyDown, false);

    if (this.exitVideoObserver) {
      this.exitVideoObserver.unobserve(this.$el);
    }

    if (this.resizeObserver) {
      this.resizeObserver.unobserve(this.$refs.overlayWrap);
    }
  }
}
</script>
